import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";

/**
 * Fetches SEO metadata from the backend and injects it into <head>.
 * The backend `/api/seo/{slug}/...` endpoints return canonical URLs,
 * OG/Twitter tags, and — for products — schema.org JSON-LD.
 *
 * `endpoint` example: `/seo/fresh-fruits/product/mango/meta`
 */
export default function SeoMeta({ endpoint, fallback }) {
  const [meta, setMeta] = useState(fallback || null);

  useEffect(() => {
    if (!endpoint) return;
    let cancelled = false;
    api.get(endpoint)
      .then(({ data }) => { if (!cancelled) setMeta(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [endpoint]);

  const m = meta || fallback || {};
  if (!m.title && !m.description) return null;

  const jsonLd = m.og_type === "product" && m.price ? {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: m.og_title || m.title,
    description: m.description,
    image: m.og_image ? [m.og_image] : undefined,
    offers: {
      "@type": "Offer",
      priceCurrency: m.currency || "INR",
      price: m.price,
      availability: m.availability === "in_stock" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      url: m.canonical,
    },
  } : null;

  return (
    <Helmet>
      {m.title && <title>{m.title}</title>}
      {m.description && <meta name="description" content={m.description} />}
      {m.keywords && <meta name="keywords" content={m.keywords} />}
      {m.robots_index === false && <meta name="robots" content="noindex, nofollow" />}
      {m.canonical && <link rel="canonical" href={m.canonical} />}
      {m.og_title && <meta property="og:title" content={m.og_title} />}
      {m.og_site_name && <meta property="og:site_name" content={m.og_site_name} />}
      {m.og_description && <meta property="og:description" content={m.og_description} />}
      {m.og_image && <meta property="og:image" content={m.og_image} />}
      {m.og_url && <meta property="og:url" content={m.og_url} />}
      {m.og_type && <meta property="og:type" content={m.og_type} />}
      <meta name="twitter:card" content={m.og_image ? "summary_large_image" : "summary"} />
      {m.og_title && <meta name="twitter:title" content={m.og_title} />}
      {m.og_description && <meta name="twitter:description" content={m.og_description} />}
      {m.og_image && <meta name="twitter:image" content={m.og_image} />}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
